// LiveTripChart.jsx
import React, { useState, useEffect } from "react";
import { Box, Grid, Card, CircularProgress, Button } from "@mui/material";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftBox from "components/SoftComponents/SoftBox";
import { Line } from "react-chartjs-2";
import moment from "moment";
import Swal from "sweetalert2";
import { getLiveTripDetail, postArrived, postAnomaly } from "modules/stopmonitor/api";

export default function LiveTripChart({ stopId, matchKey }) {
    const [tripData, setTripData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Poll the specific trip's live telemetry every 5 seconds
    useEffect(() => {
        let isMounted = true;

        const fetchDetail = async () => {
            try {
                const data = await getLiveTripDetail(matchKey, stopId);
                if (isMounted && data) {
                    setTripData(data);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Failed to load live trip details:", err);
            }
        };

        fetchDetail();
        const interval = setInterval(fetchDetail, 5000);
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [matchKey, stopId]);

    // ACTION HANDLERS FOR THE CHART VIEW
    const handleTriggerArrival = async () => {
        const currentEpoch = moment().unix();
        const formattedTime = moment.unix(currentEpoch).format("h:mm:ss a");

        const result = await Swal.fire({
            title: "Confirm Vehicle Arrival?",
            html: `
        <div style="text-align: left; font-family: sans-serif; font-size: 14px; color: #475569;">
          <p style="margin-bottom: 8px;"><strong>Route:</strong> Route ${tripData.routeShortName}</p>
          <p style="margin-bottom: 8px;"><strong>Destination:</strong> ${tripData.tripHeadsign}</p>
          <p style="margin-bottom: 8px;"><strong>Arrival Metric:</strong> ${formattedTime}</p>
        </div>
      `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Confirm",
        });

        if (result.isConfirmed) {
            try {
                await postArrived(matchKey, stopId);
                // Optimistically update the UI to show the Finalised state instantly
                setTripData(prev => ({ ...prev, arrivalEpoch: currentEpoch }));
                Swal.fire({ title: "Logged!", text: "Arrival flagged successfully.", icon: "success", timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not log arrival.", "error");
            }
        }
    };

    const handleTriggerAnomaly = async () => {
        const result = await Swal.fire({
            title: "Report Stream Anomaly?",
            html: `
        <div style="text-align: left; font-family: sans-serif; font-size: 14px; color: #475569;">
          <p><strong>Route Name:</strong> Route ${tripData.routeShortName}</p>
          <p>Flag this feed data as erratic or severely decoupled from reality?</p>
        </div>
      `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#b91c1c",
            confirmButtonText: "Report",
        });

        if (result.isConfirmed) {
            try {
                await postAnomaly(matchKey, stopId);
                setTripData(prev => ({ ...prev, isAnomaly: true }));
                Swal.fire({ title: "Reported!", text: "Anomaly registered.", icon: "success", timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not report anomaly.", "error");
            }
        }
    };


    if (loading) return <CircularProgress sx={{ display: "block", margin: "auto", mt: 10 }} />;
    if (!tripData || !tripData.historicalData?.length) {
        return <SoftTypography variant="body2">Awaiting telemetry data...</SoftTypography>;
    }

    // 1. Core Variables & Time Window
    const history = tripData.historicalData;
    const finalPoint = history[history.length - 1];
    const cutoffEpoch = finalPoint.loggedEpoch - (15 * 60);
    const recentHistory = history.filter(d => d.loggedEpoch >= cutoffEpoch);

    const staticArr = Number(matchKey.split("_")[1]);
    const hasUserArrival = !!tripData.arrivalEpoch;
    const isFinalised = hasUserArrival || tripData.isClosed;
    const isActioned = hasUserArrival || tripData.isAnomaly;

    let trueDelay = null;
    let arrivalTimeFormatted = null;

    // Map history to a sortable array of points
    let chartPoints = recentHistory.map(d => ({
        epoch: d.loggedEpoch,
        label: d.timeFormatted,
        feed1: d.feed1Delay,
        feed2: d.feed2Delay
    }));

    if (hasUserArrival) {
        trueDelay = tripData.arrivalEpoch - staticArr;
        arrivalTimeFormatted = moment.unix(tripData.arrivalEpoch).format("HH:mm:ss");

        // Inject the exact arrival time into the X-axis if it doesn't exist
        if (!chartPoints.find(p => p.label === arrivalTimeFormatted)) {
            chartPoints.push({
                epoch: tripData.arrivalEpoch,
                label: arrivalTimeFormatted,
                feed1: null, // Leaves line continuous without plotting a phantom dot
                feed2: null
            });
            // Sort chronologically so the injected point lands exactly where it should
            chartPoints.sort((a, b) => a.epoch - b.epoch);
        }
    }

    // Extract the sorted arrays for Chart.js
    const labels = chartPoints.map(p => p.label);
    const feed1Data = chartPoints.map(p => p.feed1);
    const feed2Data = chartPoints.map(p => p.feed2);

    // 2. Chart Rendering Configuration
    const chartData = {
        labels,
        datasets: [
            { label: "TCB", data: feed1Data, borderColor: "#6B4C9A", backgroundColor: "#6B4C9A", tension: 0.2, pointRadius: 2, spanGaps: true },
            { label: "GTFS-RT", data: feed2Data, borderColor: "#E67E22", backgroundColor: "#E67E22", tension: 0.2, pointRadius: 2, spanGaps: true },
        ],
    };

    const customLinesPlugin = {
        id: 'customLines',
        afterDraw: (chart) => {
            const { ctx, chartArea: { top, bottom, left, right }, scales: { x, y } } = chart;
            ctx.save();
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 6]);

            if (trueDelay !== null) {
                const yPos = y.getPixelForValue(trueDelay);
                // Relaxed the bounds check so it still draws if it's on the edge
                if (yPos !== undefined && !isNaN(yPos)) {
                    ctx.strokeStyle = '#10b981';
                    ctx.beginPath(); ctx.moveTo(left, yPos); ctx.lineTo(right, yPos); ctx.stroke();
                    ctx.fillStyle = '#10b981';
                    ctx.font = 'bold 11px sans-serif';
                    ctx.fillText(`Final Delay: ${trueDelay}s`, left + 5, yPos - 6);
                }
            }

            if (arrivalTimeFormatted !== null) {
                const xIndex = chart.data.labels.indexOf(arrivalTimeFormatted);
                if (xIndex !== -1) {
                    // FIX: Use getPixelForTick for categorical X-axes instead of getPixelForValue
                    const xPos = x.getPixelForTick(xIndex);

                    if (xPos !== undefined && !isNaN(xPos)) {
                        ctx.strokeStyle = '#ef4444';
                        ctx.beginPath(); ctx.moveTo(xPos, top); ctx.lineTo(xPos, bottom); ctx.stroke();
                        ctx.fillStyle = '#ef4444';
                        ctx.font = 'bold 11px sans-serif';
                        ctx.fillText("Arrival", xPos + 5, bottom - 5);
                    }
                }
            }
            ctx.restore();
        }
    };


    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        // Add layout padding here:
        layout: {
            padding: {
                right: 50 // Adds 50 pixels of blank space on the right side
            }
        },
        plugins: {
            legend: { position: "top" }
        },
        scales: {
            y: {
                title: { display: true, text: "Delay (sec)" },
                suggestedMin: trueDelay !== null ? Math.min(0, trueDelay - 10) : undefined,
                suggestedMax: trueDelay !== null ? trueDelay + 10 : undefined,
            }
        },
        animation: { duration: 0 }
    };

    // 3. Dynamic Scorecard Extractors
    const renderDynamicScorecard = (feedKey, feedName, color, feedStats) => {
        const currentDelay = finalPoint[`${feedKey}Delay`];
        const absolutePredEpoch = currentDelay !== null ? staticArr + currentDelay : null;

        if (isFinalised) {
            return (
                <Card sx={{ borderLeft: `5px solid ${color}`, boxShadow: 3 }}>
                    <SoftBox p={2}>
                        <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ color }}>{feedName} (Finalised)</SoftTypography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xxs={4}>
                                <SoftTypography variant="caption" color="text">Avg MAE</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">{feedStats?.mae ?? "-"}s</SoftTypography>
                            </Grid>
                            <Grid item xxs={4}>
                                <SoftTypography variant="caption" color="text">Final Err</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">{feedStats?.finalError ?? "-"}s</SoftTypography>
                            </Grid>
                            <Grid item xxs={4}>
                                <SoftTypography variant="caption" color="text">Bias</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: feedStats?.bias < 0 ? "#e53935" : "#4caf50" }}>
                                    {feedStats?.bias ?? "-"}s
                                </SoftTypography>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </Card>
            );
        } else {
            return (
                <Card sx={{ borderLeft: `5px solid ${color}`, boxShadow: 3, bgcolor: "#f8fafc" }}>
                    <SoftBox p={2}>
                        <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ color }}>{feedName} (Live Tracking)</SoftTypography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xxs={6}>
                                <SoftTypography variant="caption" color="text">Current Delay</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">
                                    {currentDelay !== null ? `${currentDelay}s` : "No Data"}
                                </SoftTypography>
                            </Grid>
                            <Grid item xxs={6}>
                                <SoftTypography variant="caption" color="text">Est. Arrival Time</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">
                                    {absolutePredEpoch ? moment.unix(absolutePredEpoch).format("HH:mm:ss") : "--:--:--"}
                                </SoftTypography>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </Card>
            );
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%", overflowY: "auto" }}>

            {/* HEADER: General Trip Info and Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <SoftTypography variant="h5" fontWeight="bold">
                        Route {tripData.routeShortName} - {tripData.tripHeadsign}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                        Trip Key: {tripData.matchKey}
                    </SoftTypography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant={hasUserArrival ? "contained" : "contained"}
                        size="small"
                        color={hasUserArrival ? "success" : "dark"}
                        disabled={isActioned}
                        onClick={handleTriggerArrival}
                        sx={{ textTransform: "none", minWidth: "120px" }}
                    >
                        {hasUserArrival ? "Arrived ✅" : "Mark Arrived"}
                    </Button>
                    <Button
                        variant={tripData.isAnomaly ? "contained" : "contained"}
                        size="small"
                        color="error"
                        disabled={isActioned}
                        onClick={handleTriggerAnomaly}
                        sx={{ textTransform: "none", minWidth: "120px" }}
                    >
                        {tripData.isAnomaly ? "Reported ⚠️" : "Anomaly"}
                    </Button>
                </Box>
            </Box>

            {/* Meta Information Badges */}
            {isFinalised && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", px: 1, mt: -1 }}>
                    {hasUserArrival && (
                        <SoftTypography variant="caption" sx={{ bgcolor: "#ecfdf5", color: "#10b981", px: 1, py: 0.5, borderRadius: 1, fontWeight: "bold" }}>
                            ✅ Marked Arrived At: {arrivalTimeFormatted} ({trueDelay}s Delay)
                        </SoftTypography>
                    )}
                    {tripData.isClosed && (
                        <SoftTypography variant="caption" sx={{ bgcolor: "#f1f5f9", color: "#64748b", px: 1, py: 0.5, borderRadius: 1, fontWeight: "bold" }}>
                            📡 Left Feed At: {moment.unix(finalPoint.loggedEpoch).format("HH:mm:ss")}
                        </SoftTypography>
                    )}
                </Box>
            )}

            {/* Dynamic Scorecards */}
            <Grid container spacing={2}>
                <Grid item xxs={12} md={6}>
                    {renderDynamicScorecard("feed1", "TCB", "#6B4C9A", tripData.stats?.feed1)}
                </Grid>
                <Grid item xxs={12} md={6}>
                    {renderDynamicScorecard("feed2", "GTFS-RT", "#E67E22", tripData.stats?.feed2)}
                </Grid>
            </Grid>

            {/* Chart Section */}
            <Box sx={{ height: 350, position: "relative" }}>
                <Line data={chartData} options={chartOptions} plugins={[customLinesPlugin]} />
            </Box>

        </Box>
    );
}